import { useState, useEffect } from 'react';
import {
  Sliders,
  RefreshCw,
  Save,
  AlertCircle,
  CheckCircle2,
  X,
  AlertTriangle,
  Film,
} from 'lucide-react';
import { credits } from '../../api/client';
import { cn } from '../../lib/utils';

interface CostEntry { cost: string; ttl: string }

interface CostForm {
  text: CostEntry;
  image: CostEntry;
  // Legacy flat video cost (kept for backwards compat)
  video: CostEntry;
  // Duration-specific video costs
  video_5s: CostEntry;
  video_10s: CostEntry;
  video_15s: CostEntry;
  video_audio_5s: CostEntry;
  video_audio_10s: CostEntry;
  video_audio_15s: CostEntry;
}

const DEFAULT_COSTS: CostForm = {
  text: { cost: '1', ttl: '60' },
  image: { cost: '10', ttl: '180' },
  video: { cost: '50', ttl: '900' },
  video_5s: { cost: '30', ttl: '900' },
  video_10s: { cost: '60', ttl: '900' },
  video_15s: { cost: '90', ttl: '900' },
  video_audio_5s: { cost: '15', ttl: '900' },
  video_audio_10s: { cost: '30', ttl: '900' },
  video_audio_15s: { cost: '45', ttl: '900' },
};

const VIDEO_COST_KEYS = [
  'video_5s', 'video_10s', 'video_15s',
  'video_audio_5s', 'video_audio_10s', 'video_audio_15s',
] as const;

export default function CreditsConfig() {
  const [costs, setCosts] = useState<CostForm>(DEFAULT_COSTS);
  const [starterCredits, setStarterCredits] = useState('500');
  const [systemEnabled, setSystemEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const [costsRes, configRes] = await Promise.all([
        credits.costs(),
        credits.config(),
      ]);

      // Populate costs from all gen_types
      const costMap: Record<string, { cost: string; ttl: string }> = {};
      for (const c of costsRes.costs) {
        costMap[c.gen_type] = { cost: String(c.cost), ttl: String(c.ttl_seconds) };
      }
      setCosts({
        text: costMap.text || DEFAULT_COSTS.text,
        image: costMap.image || DEFAULT_COSTS.image,
        video: costMap.video || DEFAULT_COSTS.video,
        video_5s: costMap.video_5s || DEFAULT_COSTS.video_5s,
        video_10s: costMap.video_10s || DEFAULT_COSTS.video_10s,
        video_15s: costMap.video_15s || DEFAULT_COSTS.video_15s,
        video_audio_5s: costMap.video_audio_5s || DEFAULT_COSTS.video_audio_5s,
        video_audio_10s: costMap.video_audio_10s || DEFAULT_COSTS.video_audio_10s,
        video_audio_15s: costMap.video_audio_15s || DEFAULT_COSTS.video_audio_15s,
      });

      // Populate config
      for (const entry of configRes.config) {
        if (entry.key === 'starter_credits') setStarterCredits(entry.value);
        if (entry.key === 'system_enabled') setSystemEnabled(entry.value === 'true');
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
      setError(err instanceof Error ? err.message : 'Failed to load config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      const parseCost = (raw: string, fallback: number): number => {
        const trimmed = raw.trim();
        if (trimmed === '') return fallback;
        return Number(trimmed);
      };

      const costEntries: { gen_type: string; cost: number; ttl_seconds: number }[] = [
        { gen_type: 'text', cost: parseCost(costs.text.cost, 1), ttl_seconds: parseInt(costs.text.ttl) || 60 },
        { gen_type: 'image', cost: parseCost(costs.image.cost, 10), ttl_seconds: parseInt(costs.image.ttl) || 180 },
        // Duration-specific video costs. (The legacy bare 'video' gen_type is intentionally
        // NOT sent: the worker whitelist rejects it, which would 400 the whole Promise.all
        // and fail every save. The duration-specific keys below replaced it.)
        ...VIDEO_COST_KEYS.map((key) => ({
          gen_type: key,
          cost: parseCost(costs[key].cost, parseInt(DEFAULT_COSTS[key].cost)),
          ttl_seconds: parseInt(costs[key].ttl) || 900,
        })),
      ];

      for (const entry of costEntries) {
        if (!Number.isSafeInteger(entry.cost)) throw new Error(`Cost for ${entry.gen_type} must be a whole number`);
        if (entry.cost < 0) throw new Error(`Cost for ${entry.gen_type} must be non-negative`);
        if (entry.ttl_seconds < 1) throw new Error(`TTL for ${entry.gen_type} must be positive`);
      }

      await Promise.all([
        ...costEntries.map((entry) => credits.updateCost(entry)),
        credits.updateConfig({ key: 'starter_credits', value: starterCredits }),
        credits.updateConfig({ key: 'system_enabled', value: String(systemEnabled) }),
      ]);

      setSuccess('Configuration saved successfully');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const updateCost = (key: keyof CostForm, field: 'cost' | 'ttl', value: string) => {
    setCosts((c) => ({ ...c, [key]: { ...c[key], [field]: value } }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading configuration...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-3xl font-bold text-gradient">Credit Configuration</h1>
        <p className="text-muted-foreground mt-1">Manage credit costs and system settings</p>
      </div>

      {/* Warning */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 flex items-center gap-3 animate-fade-in-up animate-delay-100">
        <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
        <span className="text-sm text-amber-300">Changes apply immediately to new generations</span>
      </div>

      {/* Success */}
      {success && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 flex items-center gap-3 animate-scale-in">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />
          <span className="text-sm text-emerald-300 flex-1">{success}</span>
          <button onClick={() => setSuccess(null)} className="p-1 rounded-lg hover:bg-emerald-500/20 text-emerald-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-4 flex items-center gap-3 animate-scale-in">
          <AlertCircle className="w-5 h-5 text-rose-400 flex-shrink-0" />
          <span className="text-sm text-rose-300 flex-1">{error}</span>
          <button onClick={() => setError(null)} className="p-1 rounded-lg hover:bg-rose-500/20 text-rose-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Credit Costs */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Sliders className="w-5 h-5 text-emerald-400" />
            </div>
            <h2 className="text-lg font-semibold">Credit Costs</h2>
          </div>

          <div className="space-y-6">
            <CostRow
              label="Text message"
              cost={costs.text.cost}
              ttl={costs.text.ttl}
              onCostChange={(v) => updateCost('text', 'cost', v)}
              onTtlChange={(v) => updateCost('text', 'ttl', v)}
            />
            <CostRow
              label="Image generation"
              cost={costs.image.cost}
              ttl={costs.image.ttl}
              onCostChange={(v) => updateCost('image', 'cost', v)}
              onTtlChange={(v) => updateCost('image', 'ttl', v)}
            />
          </div>
        </div>

        {/* Video Pricing */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-200">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-violet-500/20 flex items-center justify-center">
              <Film className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Video Pricing</h2>
              <p className="text-xs text-muted-foreground">Total = base + audio add-on</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Base Cost (by duration)</h3>
              <div className="space-y-4">
                <CostRow label="5 seconds" cost={costs.video_5s.cost} ttl={costs.video_5s.ttl}
                  onCostChange={(v) => updateCost('video_5s', 'cost', v)}
                  onTtlChange={(v) => updateCost('video_5s', 'ttl', v)} />
                <CostRow label="10 seconds" cost={costs.video_10s.cost} ttl={costs.video_10s.ttl}
                  onCostChange={(v) => updateCost('video_10s', 'cost', v)}
                  onTtlChange={(v) => updateCost('video_10s', 'ttl', v)} />
                <CostRow label="15 seconds" cost={costs.video_15s.cost} ttl={costs.video_15s.ttl}
                  onCostChange={(v) => updateCost('video_15s', 'cost', v)}
                  onTtlChange={(v) => updateCost('video_15s', 'ttl', v)} />
              </div>
            </div>

            <div className="border-t border-border/30 pt-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Audio Add-on</h3>
              <div className="space-y-4">
                <CostRow label="5s + audio" cost={costs.video_audio_5s.cost} ttl={costs.video_audio_5s.ttl}
                  onCostChange={(v) => updateCost('video_audio_5s', 'cost', v)}
                  onTtlChange={(v) => updateCost('video_audio_5s', 'ttl', v)} />
                <CostRow label="10s + audio" cost={costs.video_audio_10s.cost} ttl={costs.video_audio_10s.ttl}
                  onCostChange={(v) => updateCost('video_audio_10s', 'cost', v)}
                  onTtlChange={(v) => updateCost('video_audio_10s', 'ttl', v)} />
                <CostRow label="15s + audio" cost={costs.video_audio_15s.cost} ttl={costs.video_audio_15s.ttl}
                  onCostChange={(v) => updateCost('video_audio_15s', 'cost', v)}
                  onTtlChange={(v) => updateCost('video_audio_15s', 'ttl', v)} />
              </div>
            </div>
          </div>
        </div>

        {/* System Settings */}
        <div className="card-glow rounded-2xl p-6 animate-fade-in-up animate-delay-300">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-teal-500/20 flex items-center justify-center">
              <Sliders className="w-5 h-5 text-teal-400" />
            </div>
            <h2 className="text-lg font-semibold">System Settings</h2>
          </div>

          <div className="space-y-6">
            {/* Starter Credits */}
            <div>
              <label className="block text-sm font-medium mb-2">Starter Credits</label>
              <p className="text-xs text-muted-foreground mb-2">Credits given to new users automatically</p>
              <input
                type="number"
                value={starterCredits}
                onChange={(e) => setStarterCredits(e.target.value)}
                min="0"
                className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
              />
            </div>

            {/* System Enabled */}
            <div>
              <label className="block text-sm font-medium mb-2">System Enabled</label>
              <p className="text-xs text-muted-foreground mb-3">When disabled, credit checks are skipped</p>
              <button
                type="button"
                onClick={() => setSystemEnabled(!systemEnabled)}
                className={cn(
                  'relative inline-flex h-7 w-12 items-center rounded-full transition-colors',
                  systemEnabled ? 'bg-emerald-500' : 'bg-secondary'
                )}
              >
                <span
                  className={cn(
                    'inline-block h-5 w-5 rounded-full bg-white transition-transform',
                    systemEnabled ? 'translate-x-6' : 'translate-x-1'
                  )}
                />
              </button>
              <span className={cn('ml-3 text-sm', systemEnabled ? 'text-emerald-400' : 'text-muted-foreground')}>
                {systemEnabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end animate-fade-in-up animate-delay-400">
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 px-8 py-3 rounded-xl bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 text-sm font-medium transition-colors disabled:opacity-50"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Changes
        </button>
      </div>
    </div>
  );
}

function CostRow({
  label,
  cost,
  ttl,
  onCostChange,
  onTtlChange,
}: {
  label: string;
  cost: string;
  ttl: string;
  onCostChange: (v: string) => void;
  onTtlChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium mb-2">{label}</label>
      <div className="flex gap-3">
        <div className="flex-1">
          <label className="block text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Credits</label>
          <input
            type="number"
            value={cost}
            onChange={(e) => onCostChange(e.target.value)}
            min="0"
            className="w-full px-3 py-2 rounded-xl bg-secondary border border-border/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
          />
        </div>
        <div className="flex-1">
          <label className="block text-[10px] text-muted-foreground uppercase tracking-wider mb-1">TTL (seconds)</label>
          <input
            type="number"
            value={ttl}
            onChange={(e) => onTtlChange(e.target.value)}
            min="1"
            className="w-full px-3 py-2 rounded-xl bg-secondary border border-border/50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
          />
        </div>
      </div>
    </div>
  );
}
